import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useSubscription } from "@apollo/client";
import { OcRoleFilterItemData } from "../role-filter/role-filter.types";
import { OcRoleFilterSubtypeProps } from "./role-filter-subtype";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { useOcPolling } from "oc/hooks";
import {
  OC_ROLE_FILTER_ENTITY_DEFINITIONS,
  OC_ROLE_FILTER_QUERIES,
  OC_ROLE_FILTER_RETURN_ENTITY_DEFINITIONS,
  OC_ROLE_FILTER_SUBSCRIPTIONS,
} from "oc/utils";

type EntityKeys =
  (typeof OC_ROLE_FILTER_ENTITY_DEFINITIONS)[keyof typeof OC_ROLE_FILTER_ENTITY_DEFINITIONS];
type ShorthandedRED = typeof OC_ROLE_FILTER_RETURN_ENTITY_DEFINITIONS; // RED = Return entity definitions

type ReturnEntityPossibleKeys =
  ShorthandedRED[keyof ShorthandedRED][keyof ShorthandedRED[keyof ShorthandedRED]];

type GraphQLReturnT = Partial<
  Record<ReturnEntityPossibleKeys, OcRoleFilterSubtypeGraphQLData>
>;
type OcRoleFilterQueryParams = {
  page: number;
  pageSize: number;
  searchField: "name";
  searchText: string;
  product: ProductsT;
  role: RoleCategoriesT;
  showAllowedEntities: boolean;
} & Partial<Record<EntityKeys, string[]>>;

export interface OcRoleFilterSubtypeGraphQLData {
  availableList: OcRoleFilterItemData[];
  selectedList: OcRoleFilterItemData[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  __typename: string;
  _id: string | null; // TODO f could be null
}

const getUniqueItemsById = (list: OcRoleFilterItemData[]) => {
  const seen = new Set<string>();
  return list.filter((item) => {
    const id = String(item._id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const useOcRoleFilterSubtype = ({
  type,
  queryType,
  isCheckbox,
  editable = true,
  handleAddItem,
  handleRemoveItem,
  roleFilters,
  product,
  role,
}: OcRoleFilterSubtypeProps) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState<OcRoleFilterSubtypeGraphQLData | undefined>(
    undefined,
  );

  const queryParams: OcRoleFilterQueryParams = useMemo(() => {
    let querySearchText = "";
    if (searchText.length >= 3) querySearchText = searchText;
    return {
      page: 1,
      pageSize,
      searchField: "name",
      searchText: querySearchText,
      [OC_ROLE_FILTER_ENTITY_DEFINITIONS[type]]: roleFilters[type],
      product,
      role,
      showAllowedEntities: editable,
    };
  }, [pageSize, searchText, product, role, roleFilters, editable, type]); // Editable and type could be removed?

  const {
    data: rawData,
    loading,
    fetchMore,
  } = useQuery<GraphQLReturnT>(OC_ROLE_FILTER_QUERIES[queryType][type], {
    fetchPolicy: "no-cache",
    pollInterval: useOcPolling(1000 * 60),
    variables: queryParams,
  });

  useSubscription(OC_ROLE_FILTER_SUBSCRIPTIONS[type], {
    skip: !rawData,
    fetchPolicy: "network-only",
    variables: queryParams,
  });

  useEffect(() => {
    if (rawData)
      // Only change the stored data, if the data isn't null / undefined.
      setData(
        rawData[OC_ROLE_FILTER_RETURN_ENTITY_DEFINITIONS[queryType][type]],
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData]);

  // This function cleans up the list a bit if needed.
  // For example, import contract query returns the names with pipes.
  const cleanList = (list: OcRoleFilterItemData[]) => {
    let cleanList = list;

    // Theoretically this 'if' isn't required, but it tells the story
    // about why this code was written.
    if (type === "contract" && queryType === "import") {
      const parsedAvailableList: OcRoleFilterItemData[] = list.map((item) => ({
        ...item,
        name: `${item.name.replaceAll("|", " ").trim()}`,
      }));
      cleanList = parsedAvailableList;
    }

    cleanList = cleanList.map((item) => {
      const newItem = { ...item };
      if (newItem.name === "oc_empty") newItem.name = t("OC_EMPTY");
      return newItem;
    });

    // Filter out "not_defined" items (closed/deleted entities from backend)
    cleanList = cleanList.filter((item) => item.name !== "not_defined");

    return cleanList;
  };

  const allItems = useMemo(() => {
    if (!data) return [];
    const merged = [...data.availableList, ...data.selectedList];
    const cleaned = cleanList(merged);
    return getUniqueItemsById(cleaned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const availableItems = useMemo(() => {
    if (data) {
      const cleaned = cleanList(data.availableList);
      return getUniqueItemsById(cleaned);
    } else return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectedItems = useMemo(() => {
    if (!data) return [];

    if (roleFilters[type]?.length === 0 && isCheckbox) {
      return availableItems;
    }

    const fromServer = cleanList(data.selectedList);
    const baseSelected = getUniqueItemsById(fromServer);

    const codes = roleFilters[type] ?? [];
    const normalizedCodes = codes.filter((code) => code !== "-");
    if (normalizedCodes.length === 0) {
      return baseSelected;
    }

    const existingCodes = new Set(baseSelected.map((item) => item.code));
    const result = [...baseSelected];

    normalizedCodes.forEach((code) => {
      if (!existingCodes.has(code)) {
        const found = allItems.find((item) => item.code === code);
        if (found) {
          result.push(found);
          existingCodes.add(code);
        }
      }
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, roleFilters, type, isCheckbox, availableItems, allItems, t]);

  const isAllSelected = useMemo(
    () => selectedItems.length === availableItems.length && isCheckbox,
    [selectedItems, availableItems, isCheckbox],
  );

  const handleClickItem = (item: OcRoleFilterItemData) => {
    if (isAllSelected) {
      const filteredResults = availableItems.filter(
        (listItem) => listItem.code !== item.code,
      );

      const filteredCodes = filteredResults.map((listItem) => listItem.code);

      handleAddItem({
        key: type,
        value: filteredCodes,
      });

      return;
    }

    if (selectedItems.find((listItem) => listItem._id === item._id)) {
      handleRemoveItem({
        key: type,
        value: item.code,
      });
    } else {
      handleAddItem({
        key: type,
        value: item.code,
      });
    }
  };

  const handleCheckAll = () => {
    let value = ["-"];
    if (!isAllSelected) value = [];
    handleAddItem({
      key: type,
      value,
    });
  };

  const onSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onClearSearchText = () => {
    setSearchText("");
  };

  const handleFetchMore = () => {
    if (data && availableItems.length >= data?.total) return;
    setPageSize((currentPageSize) => currentPageSize + 10);
    fetchMore({});
  };

  const isDisabled = availableItems.length === 0 && searchText.length === 0;

  const dataTotal = data?.total;

  const handleOpen = () => {
    setOpened(!opened);
  };

  // F After revisiting this, I'm not sure this is needed. Seems like the backend
  // doesn't send back "oc_empty" if it's non editable.
  /*
    TODO f if non-editable, this should be used.
    const filteredSelectedItems = selectedItems.filter(
      (item) => item.name !== "empty",
    );*/

  return {
    opened,
    setOpened,
    handleOpen,
    loading,
    dataTotal,
    availableItems,
    selectedItems,
    handleClickItem,
    onSearchTextChange,
    onClearSearchText,
    setSearchText,
    searchText,
    handleFetchMore,
    handleCheckAll,
    isDisabled,
  };
};
